// src/services/ListingsService.js
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc, 
  doc, 
  query, 
  orderBy, 
  serverTimestamp, 
  where,
  deleteDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase';

const LISTINGS_COLLECTION = 'listings';

// Add a new listing
export const addListing = async (listingData, userId) => {
  try {
    const docRef = await addDoc(collection(db, LISTINGS_COLLECTION), {
      ...listingData,
      userId,
      locations: Array.isArray(listingData.locations) ? listingData.locations : [],
      archived: false,
      archivedAt: null,
      currentAttendees: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error("Error adding listing:", error);
    throw error;
  }
};

// Get all listings (limited info for public view)
// Excludes pending/rejected listings awaiting provider-application review;
// listings without a status field are legacy and treated as published.
export const getListings = async () => {
  try {
    const q = query(
      collection(db, LISTINGS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore timestamps to regular Date objects
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        eventStart: doc.data().eventStart?.toDate(),
        eventEnd: doc.data().eventEnd?.toDate(),
        archivedAt: doc.data().archivedAt?.toDate()
      }))
      .filter(listing =>
        listing.archived !== true &&
        listing.status !== 'pending' &&
        listing.status !== 'rejected'
      );
  } catch (error) {
    console.error("Error getting listings:", error);
    throw error;
  }
};

// Get all listings for admin views, including pending/rejected ones
export const getAllListings = async () => {
  try {
    const q = query(
      collection(db, LISTINGS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        eventStart: doc.data().eventStart?.toDate(),
        eventEnd: doc.data().eventEnd?.toDate(),
        archivedAt: doc.data().archivedAt?.toDate()
      }))
      .filter(listing => listing.archived !== true);
  } catch (error) {
    console.error("Error getting all listings:", error);
    throw error;
  }
};

// Get a single listing by ID
export const getListingById = async (listingId) => {
  try {
    const docRef = doc(db, LISTINGS_COLLECTION, listingId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error("Listing not found");
    }
    
    return {
      id: docSnap.id,
      ...docSnap.data(),
      // Convert Firestore timestamps to regular Date objects
      createdAt: docSnap.data().createdAt?.toDate(),
      updatedAt: docSnap.data().updatedAt?.toDate(),
      eventStart: docSnap.data().eventStart?.toDate(),
      eventEnd: docSnap.data().eventEnd?.toDate(),
      archivedAt: docSnap.data().archivedAt?.toDate()
    };
  } catch (error) {
    console.error("Error getting listing:", error);
    throw error;
  }
};

// Get listings by user ID
export const getListingsByUserId = async (userId) => {
  try {
    const q = query(
      collection(db, LISTINGS_COLLECTION),
      where("userId", "==", userId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
      eventStart: doc.data().eventStart?.toDate(),
      eventEnd: doc.data().eventEnd?.toDate(),
      archivedAt: doc.data().archivedAt?.toDate()
    }));
  } catch (error) {
    console.error("Error getting user listings:", error);
    throw error;
  }
};

// Update a listing
export const updateListing = async (listingId, updatedData) => {
  try {
    const listingRef = doc(db, LISTINGS_COLLECTION, listingId);
    await updateDoc(listingRef, {
      ...updatedData,
      updatedAt: serverTimestamp()
    });
    return listingId;
  } catch (error) {
    console.error("Error updating listing:", error);
    throw error;
  }
};

// Delete a listing
export const deleteListing = async (listingId) => {
  try {
    const listingRef = doc(db, LISTINGS_COLLECTION, listingId);
    await deleteDoc(listingRef);
    return true;
  } catch (error) {
    console.error("Error deleting listing:", error);
    throw error;
  }
};

// Archive (soft delete) a listing
export const archiveListing = async (listingId) => {
  try {
    const listingRef = doc(db, LISTINGS_COLLECTION, listingId);
    await updateDoc(listingRef, {
      archived: true,
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error("Error archiving listing:", error);
    throw error;
  }
};

// Restore an archived listing
export const restoreListing = async (listingId) => {
  try {
    const listingRef = doc(db, LISTINGS_COLLECTION, listingId);
    await updateDoc(listingRef, {
      archived: false,
      archivedAt: null,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error("Error restoring listing:", error);
    throw error;
  }
};
